import type { MedicationRequest } from '@medplum/fhirtypes'

export interface DraftPharmacy {
  ncpdpid: string
  storeName: string
  searchText: string
}

interface DispenserInput {
  ncpdpid?: string | null
  storeName?: string | null
  city?: string | null
  zip?: string | null
}

/*
 * Elation cannot store a pharmacy on a draft medication (fulfillment is
 * read-only). The previous dispenser is the NCPDP id on the prior order,
 * and the sidecar types `searchText` into the prescription form.
 */
export function toDraftPharmacy(
  input: DispenserInput
): DraftPharmacy | undefined {
  const ncpdpid = input.ncpdpid?.trim()
  const storeName = input.storeName?.trim()
  if (!ncpdpid || !storeName) {
    return undefined
  }
  return {
    ncpdpid,
    storeName,
    searchText: draftPharmacySearchText({
      storeName,
      city: input.city,
      zip: input.zip,
    }),
  }
}

export function previousDispenserNcpdpid(
  fulfillment: { pharmacy_ncpdpid?: string | null } | null | undefined
): string | undefined {
  const ncpdpid = fulfillment?.pharmacy_ncpdpid?.trim()
  return ncpdpid ? ncpdpid : undefined
}

export function previousDispenserNcpdpidFromExternalJson(
  externalResourceJSON: string | undefined
): string | undefined {
  if (!externalResourceJSON) {
    return undefined
  }
  try {
    const parsed = JSON.parse(externalResourceJSON) as {
      fulfillment?: { pharmacy_ncpdpid?: string | null } | null
    }
    return previousDispenserNcpdpid(parsed.fulfillment)
  } catch {
    return undefined
  }
}

export function draftPharmacyFromStamp(
  stamp: string | null | undefined
): DraftPharmacy | undefined {
  if (!stamp) {
    return undefined
  }
  try {
    const parsed = JSON.parse(stamp) as {
      ncpdpid?: string
      storeName?: string
      searchText?: string
    }
    const ncpdpid = parsed.ncpdpid?.trim()
    const storeName = parsed.storeName?.trim()
    const searchText = parsed.searchText?.trim()
    if (!ncpdpid || !storeName || !searchText) {
      return undefined
    }
    return { ncpdpid, storeName, searchText }
  } catch {
    return undefined
  }
}

export function draftPharmacyFromMedication(medication: {
  externalResourceJSON?: string
  dispenseRequest?: MedicationRequest['dispenseRequest']
  previousDispenser?: string | null
}): DraftPharmacy | undefined {
  const currentNcpdpid = previousDispenserNcpdpidFromExternalJson(
    medication.externalResourceJSON
  )
  if (currentNcpdpid) {
    return toDraftPharmacy({
      ncpdpid: currentNcpdpid,
      storeName: medication.dispenseRequest?.performer?.display,
    })
  }
  return draftPharmacyFromStamp(medication.previousDispenser)
}

function draftPharmacySearchText({
  storeName,
  city,
  zip,
}: {
  storeName: string
  city?: string | null
  zip?: string | null
}): string {
  const postalCode = zip?.trim()
  if (postalCode) {
    return `${storeName} ${postalCode}`
  }
  const locality = city?.trim()
  if (locality) {
    return `${storeName} ${locality}`
  }
  return storeName
}
